import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

export interface CommunicateRequest {
  message: string;
  property_id?: string;
  location_id?: string;
  document_id?: string;
  chat_type?: string; // 'general' | 'documents'
}

export interface CommunicateResponse {
  message?: string;
  session_id: string;
  email?: string;
  urgent?: boolean;
  intent?: string;
}

export type ApiRequest_Communicate = CommunicateRequest;
export type ApiResponse_Communicate = CommunicateResponse;

const METHOD = 'POST';
const ENDPOINT = 'simulation/communicate';
export const QUERY_KEY = () => getQueryKey(METHOD, ENDPOINT, {});

export const useCommunicate = (stateFns?: MutationStateFns<ApiResponse_Communicate>) => {
  const { session } = useAuth();

  return useMutation<ApiResponse_Communicate, AxiosError, ApiRequest_Communicate>({
    mutationFn: async (data: ApiRequest_Communicate) => {
      try {
        const response = await handleApiCall<ApiResponse_Communicate, ApiRequest_Communicate>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {}),
          data,
          {
            timeout: 60000, // 60 seconds timeout for AI responses
            signal: AbortSignal.timeout(60000), // Additional abort signal for timeout
          }
        );

        return response;
      } catch (err) {
        const axiosError = err as AxiosError;

        // Handle different types of errors with appropriate messages
        if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
          enqueueSnackbar('Request timed out. Please try again.', { variant: 'error' });
        } else if (axiosError.code === 'ERR_CANCELED' || axiosError.message?.includes('aborted')) {
          enqueueSnackbar('Request was cancelled. Please try again.', { variant: 'error' });
        } else if (axiosError.code === 'ERR_NETWORK') {
          enqueueSnackbar('Network error. Please check your connection and try again.', {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Failed to send message. Please try again.', { variant: 'error' });
        }

        throw err;
      }
    },
    onSuccess: data => {
      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      stateFns?.onError?.(err);
    },
  });
};
